// src/pages/ObsGraphics.jsx
// Grafiche broadcast automatiche per OBS (browser source, sfondo trasparente).
//   /obs/graphics            → regia automatica: scene a rotazione + interruzioni
//                              su eventi (giro veloce, personal best)
//   /obs/graphics/:scene     → scena fissa: status | gaps | fastest | battle |
//                              topspeed | sectors | podium | driver | heart | lapevent
// Query: ?bg=green (default, chroma key) | transparent | dark  ?pos=bl|br|tl|tr|bc|tc  ?interval=9
//        ?id=<pilotId> (scena driver)  ?rows=8 (scene gaps/heart)
//        ?battle=2.5 (gap massimo in secondi per considerare un duello)
//        ?format=vertical → 9:16 per dirette Instagram/YouTube (Browser Source
//        1080x1920): grafica compatta nella fascia bassa, sopra i commenti.
//        ?band=300 altezza della fascia · ?pos=t*|mc la sposta in alto o al
//        centro · ?safe=0 usa tutto lo schermo
// Dati: WebSocket del backend (race_snapshot 1 Hz + telemetry ~15 Hz), nessuna
// scrittura. Tutte le statistiche sono calcolate lato client.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { colorFromName, chromaSafeColor } from '../utils/colors';
import logoRaceSense from '../img/logo-racesense-live.svg';

import { Icon } from '../components/icons';
import VerticalStage from '../components/obs/VerticalStage';
import { isVerticalFormat, stageBox, verticalPos } from '../utils/obsFormat';
import './ObsGraphics.css';

// Green screen (default): i colori verdi vengono spostati fuori dalla zona del
// chroma key, altrimenti OBS li renderebbe trasparenti insieme allo sfondo.
let _keySafe = true;
const paint = (c) => (_keySafe ? chromaSafeColor(c) : c);

const WS_URL = process.env.REACT_APP_WS_URL || (window.location.protocol === 'https:'
  ? `wss://${window.location.hostname}/ws`
  : `ws://${window.location.hostname}:5001`);

const ROTATION = ['status', 'gaps', 'fastest', 'battle', 'topspeed', 'sectors', 'heart', 'driver'];
const HR_MAX_REF = 200;            // FC massima di riferimento per le zone cardio
// hr valido = fascia agganciata con battito; 0/null = pilota senza sensore
const hrOf = (d) => (Number.isFinite(d?.hr) && d.hr > 0) ? d.hr : null;
const hrZone = (hr) => {
  if (!Number.isFinite(hr) || hr <= 0) return null;
  const p = hr / HR_MAX_REF;
  if (p < 0.6) return { z: 1, label: 'RECUPERO', color: '#38bdf8' };
  if (p < 0.7) return { z: 2, label: 'LEGGERO', color: _keySafe ? '#818cf8' : '#22c55e' };
  if (p < 0.8) return { z: 3, label: 'AEROBICO', color: _keySafe ? '#ffc105' : '#c0ff03' };
  if (p < 0.9) return { z: 4, label: 'SOGLIA', color: '#ff9800' };
  return { z: 5, label: 'MASSIMALE', color: '#ef4444' };
};
const BATTLE_MAX_GAP_S = 2.5;      // due piloti sono "in battaglia" sotto questo distacco
const TREND_WINDOW_MS = 30000;     // finestra per la tendenza del gap
const EVENT_TTL_MS = 8000;         // durata delle grafiche evento (giro veloce, PB)

const keyOf = (d) => (d && (d.id || d.pilotId)) || (d && d.mac) || '';
const formatLap = (s) => {
  if (!Number.isFinite(s)) return '—';
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(3);
  return `${m}:${sec.padStart(6, '0')}`;
};
const formatCountdown = (seconds) => {
  if (!Number.isFinite(seconds)) return '--:--';
  const h = Math.floor(seconds / 3600), m = Math.floor((seconds % 3600) / 60), s = seconds % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};
// "+12.34" → 12.34 ; "+1L" / "LEADER" / "—" → null
const parseGap = (g) => {
  if (typeof g !== 'string') return null;
  const m = g.match(/^\+?(-?\d+(?:\.\d+)?)$/);
  return m ? parseFloat(m[1]) : null;
};
const displayName = (d) => {
  const cleaned = String(d?.fullName || '').replace(/[/\\]+/g, ' ').replace(/\s+/g, ' ').trim();
  return cleaned || cleanTeam(d?.team) || d?.tag || d?.mac || '';
};
// Team "vuoto" (placeholder tipo "_" o "/") → stringa vuota, così non va in onda.
const cleanTeam = (t) => {
  const s = String(t || '').trim();
  return /^[\W_]*$/.test(s) ? '' : s;
};
// Tempo settore plausibile: scarta i valori spuri (es. pochi decimi) che il motore
// registra al primo passaggio o su un cambio di zona irregolare.
const plausibleSector = (t, globalBest) => Number.isFinite(t) && t >= Math.max(3, (Number.isFinite(globalBest) ? globalBest : 60) * 0.12);
const shortName = (d) => String(displayName(d)).replace(/\s+/g, '').slice(0, 3).toUpperCase() || '—';
const flagInfo = (status) => {
  const s = String(status || 'IN CORSO').toUpperCase();
  if (s === 'RED FLAG') return { cls: 'red', label: 'BANDIERA ROSSA' };
  if (s === 'YELLOW FLAG') return { cls: 'yellow', label: 'BANDIERA GIALLA' };
  if (s === 'FINITA') return { cls: 'chequered', label: 'BANDIERA A SCACCHI' };
  if (s === 'FORMATION LAP') return { cls: 'formation', label: 'FORMATION LAP' };
  return { cls: 'green', label: 'GARA IN CORSO' };
};

/* ---------------------------------------------------------------------------
   Feed dati: snapshot 1 Hz + statistiche derivate (top speed, storico gap,
   eventi giro). Riconnessione automatica come le altre viste OBS.
--------------------------------------------------------------------------- */
export function useRaceFeed() {
  const [snapshot, setSnapshot] = useState(null);
  const [topSpeeds, setTopSpeeds] = useState({});   // id -> { max, name, tag, color }
  const [events, setEvents] = useState([]);         // ultimi eventi giro
  const topRef = useRef({});
  const prevRef = useRef(new Map());                // id -> { lapCount, bestLapTime }
  const gapHistRef = useRef(new Map());             // id -> [{ t, gap }] (gap dal pilota davanti)
  const globalBestRef = useRef(null);

  useEffect(() => {
    let cancelled = false, ws = null, timer = null;
    const onMessage = (ev) => {
      let data; try { data = JSON.parse(ev.data); } catch { return; }
      if (data?.type === 'telemetry' && Array.isArray(data.drivers)) {
        data.drivers.forEach(d => {
          const k = keyOf(d); if (!k) return;
          const v = Number(d.speedKmh) || 0;
          const cur = topRef.current[k];
          if (!cur || v > cur.max) topRef.current[k] = { ...(cur || {}), max: v };
        });
        return;
      }
      if (data?.type === 'race_snapshot') {
        const now = Date.now();
        const drivers = Array.isArray(data.drivers) ? data.drivers : [];
        const sorted = [...drivers].sort((a, b) => (a.position || 99) - (b.position || 99));
        const newEvents = [];
        sorted.forEach((d, i) => {
          const k = keyOf(d);
          // nomi/colori per la top speed
          topRef.current[k] = { ...(topRef.current[k] || { max: 0 }), name: displayName(d), tag: d.tag, color: paint(colorFromName(d.fullName || d.tag || d.mac)) };
          // eventi: giro completato
          const prev = prevRef.current.get(k);
          if (prev && Number.isFinite(d.lapCount) && d.lapCount > prev.lapCount && Number.isFinite(d.lastLapTime)) {
            const pb = Number.isFinite(d.bestLapTime) && Math.abs(d.bestLapTime - d.lastLapTime) < 0.0005 && d.lapCount > 1;
            const fastest = Number.isFinite(data.globalBestLap) && Math.abs(data.globalBestLap - d.lastLapTime) < 0.0005
              && (globalBestRef.current == null || d.lastLapTime < globalBestRef.current);
            newEvents.push({ id: k, ts: now, name: displayName(d), tag: d.tag, team: d.team, position: d.position, lapNum: d.lapCount, lapTime: d.lastLapTime, pb, fastest, color: paint(colorFromName(d.fullName || d.tag || d.mac)) });
          }
          prevRef.current.set(k, { lapCount: d.lapCount, bestLapTime: d.bestLapTime });
          // storico gap dal pilota davanti (solo gap numerici, stesso giro)
          if (i > 0) {
            const g = parseGap(d.gapToLeader), ga = parseGap(sorted[i - 1].gapToLeader) ?? 0;
            const arr = gapHistRef.current.get(k) || [];
            if (g != null) arr.push({ t: now, gap: Math.max(0, g - ga) });
            while (arr.length && now - arr[0].t > 120000) arr.shift();
            gapHistRef.current.set(k, arr);
          }
        });
        if (Number.isFinite(data.globalBestLap)) globalBestRef.current = data.globalBestLap;
        if (newEvents.length) setEvents(ev => [...newEvents, ...ev].slice(0, 6));
        setTopSpeeds({ ...topRef.current });
        setSnapshot(data);
        return;
      }
      if (data?.type === 'race_inactive') { setSnapshot(null); }
    };
    const connect = () => {
      if (cancelled) return;
      ws = new WebSocket(WS_URL + '?role=viewer');
      ws.onmessage = onMessage;
      ws.onclose = () => { if (!cancelled) timer = setTimeout(connect, 1500); };
      ws.onerror = () => { };
    };
    connect();
    return () => { cancelled = true; if (timer) clearTimeout(timer); try { ws && ws.close(); } catch { } };
  }, []);

  const gapTrend = (id) => {
    const arr = gapHistRef.current.get(id) || [];
    if (arr.length < 2) return null;
    const now = Date.now();
    const old = arr.find(p => now - p.t <= TREND_WINDOW_MS) || arr[0];
    const last = arr[arr.length - 1];
    if (!old || old === last) return null;
    return { delta: last.gap - old.gap, seconds: Math.round((last.t - old.t) / 1000) };
  };

  return { snapshot, topSpeeds, events, gapTrend };
}

/* ---------------------------------------------------------------------------
   Statistiche derivate dallo snapshot
--------------------------------------------------------------------------- */
export function useStats(snapshot, topSpeeds, gapTrend, battleMaxGap = BATTLE_MAX_GAP_S) {
  return useMemo(() => {
    const drivers = Array.isArray(snapshot?.drivers) ? [...snapshot.drivers].sort((a, b) => (a.position || 99) - (b.position || 99)) : [];
    const leader = drivers[0] || null;
    const globalBest = Number.isFinite(snapshot?.globalBestLap) ? snapshot.globalBestLap : null;
    const fastest = globalBest != null ? drivers.find(d => Number.isFinite(d.bestLapTime) && Math.abs(d.bestLapTime - globalBest) < 0.0005) || null : null;
    let fastestLapNum = null;
    if (fastest && Array.isArray(fastest.lapTimes)) {
      const idx = fastest.lapTimes.findIndex(t => Math.abs(t - globalBest) < 0.0005);
      if (idx >= 0) fastestLapNum = fastest.lapCount - (fastest.lapTimes.length - 1 - idx);
    }
    // battaglie: coppie consecutive con gap numerico sotto soglia
    const battles = [];
    for (let i = 1; i < drivers.length; i++) {
      const g = parseGap(drivers[i].gapToLeader), ga = parseGap(drivers[i - 1].gapToLeader) ?? 0;
      if (g == null) continue;
      const gap = Math.max(0, g - ga);
      if (gap <= battleMaxGap) battles.push({ ahead: drivers[i - 1], behind: drivers[i], gap, trend: gapTrend(keyOf(drivers[i])) });
    }
    battles.sort((a, b) => a.gap - b.gap);
    // top speed
    const speeds = Object.entries(topSpeeds || {}).filter(([, v]) => v && v.max > 0).map(([id, v]) => ({ id, ...v })).sort((a, b) => b.max - a.max);
    // settori migliori
    const bestSectors = {};
    ['S1', 'S2', 'S3'].forEach(S => {
      let best = null;
      drivers.forEach(d => {
        const cands = [];
        (Array.isArray(d.sectorHistory) ? d.sectorHistory : []).forEach(h => { if (Number.isFinite(h?.[S])) cands.push(h[S]); });
        if (Number.isFinite(d.lastSectorTimes?.[S])) cands.push(d.lastSectorTimes[S]);
        cands.forEach(t => { if (plausibleSector(t, globalBest) && (!best || t < best.time)) best = { time: t, driver: d }; });
      });
      bestSectors[S] = best;
    });
    const ideal = ['S1', 'S2', 'S3'].every(S => bestSectors[S]) ? bestSectors.S1.time + bestSectors.S2.time + bestSectors.S3.time : null;
    // cardio: solo piloti con fascia (hr live o almeno un valore nella sessione)
    const hearts = drivers
      .filter(d => hrOf(d) != null || Number.isFinite(d.hrMax))
      .map(d => ({ driver: d, hr: hrOf(d), hrMax: Number.isFinite(d.hrMax) ? d.hrMax : null, hrAvg: Number.isFinite(d.hrAvg) ? d.hrAvg : null, zone: hrZone(hrOf(d)) }))
      .sort((a, b) => (b.hr ?? -1) - (a.hr ?? -1) || (b.hrMax ?? 0) - (a.hrMax ?? 0));
    return { drivers, leader, globalBest, fastest, fastestLapNum, battles, speeds, bestSectors, ideal, hearts };
  }, [snapshot, topSpeeds, gapTrend, battleMaxGap]);
}

/* ---------------------------------------------------------------------------
   Scene
--------------------------------------------------------------------------- */
function Plate({ kicker, title, children, accent = 'red', wide = false, className = '' }) {
  return (
    <div className={`og-plate og-plate--${accent} ${wide ? 'og-plate--wide' : ''} ${className}`}>
      {(kicker || title) && (
        <div className="og-plate__head">
          {kicker && <span className="og-kicker">{kicker}</span>}
          {title && <span className="og-title">{title}</span>}
        </div>
      )}
      {children}
    </div>
  );
}

function StatusScene({ snapshot, leader }) {
  const f = flagInfo(snapshot?.raceStatus);
  const isFormation = !!snapshot?.isFormationLap;
  const totalLaps = snapshot?.totalLaps;
  const lap = leader?.lapCount ?? 0;
  let dist;
  if (snapshot?.raceDurationMinutes) dist = !isFormation && Number.isFinite(snapshot.remainingSeconds) ? formatCountdown(snapshot.remainingSeconds) : `${snapshot.raceDurationMinutes}:00`;
  else dist = totalLaps ? `LAP ${lap}/${totalLaps}` : `LAP ${lap}`;
  return (
    <div className={`og-status og-status--${f.cls}`}>
      <span className="og-status__flag" />
      <div className="og-status__text">
        <span className="og-status__label">{isFormation ? 'FORMATION LAP' : f.label}{snapshot?.yellowFlagAuto ? ' · SAFETY IN PISTA' : ''}</span>
        <span className="og-status__event">{snapshot?.eventName || snapshot?.circuit?.name || 'RaceSense Live'}{snapshot?.eventName && snapshot?.circuit?.name ? ` · ${snapshot.circuit.name}` : ''}</span>
      </div>
      <span className="og-status__lap">{dist}</span>
    </div>
  );
}

function GapsScene({ drivers, rows = 8, globalBest }) {
  const list = drivers.slice(0, rows);
  const maxGap = Math.max(0.5, ...list.map(d => parseGap(d.gapToLeader) || 0));
  return (
    <Plate kicker="Live · Classifica" title="Distacchi">
      <div className="og-rows">
        {list.map((d, i) => {
          const g = parseGap(d.gapToLeader);
          const fast = globalBest != null && Number.isFinite(d.bestLapTime) && Math.abs(d.bestLapTime - globalBest) < 0.0005;
          return (
            <div className={`og-row ${i === 0 ? 'is-leader' : ''}`} key={keyOf(d)}>
              <span className={`og-pos og-pos--${Math.min(i + 1, 4)}`}>{i + 1}</span>
              <span className="og-color" style={{ background: paint(colorFromName(d.fullName || d.tag || d.mac)) }} />
              <span className="og-name"><strong>{shortName(d)}</strong><span>{displayName(d)}</span></span>
              <span className="og-bar"><span style={{ width: `${i === 0 ? 100 : Math.max(4, 100 - (g == null ? 100 : (g / maxGap) * 100))}%` }} /></span>
              <span className={`og-gap ${fast ? 'is-fast' : ''}`}>{i === 0 ? 'LEADER' : (d.gapToLeader || '—')}{d.inPit ? ' PIT' : ''}</span>
            </div>
          );
        })}
      </div>
    </Plate>
  );
}

function FastestScene({ fastest, globalBest, lapNum, isNew }) {
  if (!fastest) return <Plate kicker="Fastest lap" title="In attesa del primo giro"><div className="og-empty">Nessun giro completato</div></Plate>;
  return (
    <Plate kicker={isNew ? '● NUOVO GIRO VELOCE' : 'Fastest lap'} accent="purple" className={isNew ? 'is-flash' : ''}>
      <div className="og-big">
        <span className="og-big__time">{formatLap(globalBest)}</span>
        <span className="og-big__who">
          <span className="og-color" style={{ background: paint(colorFromName(fastest.fullName || fastest.tag || fastest.mac)) }} />
          <strong>{displayName(fastest)}</strong>
          <span>{[cleanTeam(fastest.team), lapNum ? `GIRO ${lapNum}` : ''].filter(Boolean).join(' · ')}</span>
        </span>
      </div>
    </Plate>
  );
}

function BattleScene({ battles, maxGap = BATTLE_MAX_GAP_S }) {
  const b = battles[0];
  if (!b) return <Plate kicker="Battle" title="Nessun duello serrato"><div className="og-empty">Distacchi superiori a {maxGap.toFixed(1)}s</div></Plate>;
  const trend = b.trend;
  const closing = trend && trend.delta < -0.05;
  const opening = trend && trend.delta > 0.05;
  return (
    <Plate kicker={`Battle for P${b.ahead.position}`} accent="lime">
      <div className="og-duel">
        <div className="og-duel__driver"><span className="og-pos">{b.ahead.position}</span><span className="og-color" style={{ background: paint(colorFromName(b.ahead.fullName || b.ahead.tag || b.ahead.mac)) }} /><strong>{displayName(b.ahead)}</strong></div>
        <div className="og-duel__vs"><span>VS</span><span className="og-duel__gap">{b.gap.toFixed(2)}s</span></div>
        <div className="og-duel__driver"><span className="og-pos">{b.behind.position}</span><span className="og-color" style={{ background: paint(colorFromName(b.behind.fullName || b.behind.tag || b.behind.mac)) }} /><strong>{displayName(b.behind)}</strong></div>
      </div>
      {(hrOf(b.ahead) != null || hrOf(b.behind) != null) && (
        <div className="og-duel__hr">
          <HeartBpm hr={hrOf(b.ahead)} />
          <span className="og-duel__hr-label">SOTTO PRESSIONE</span>
          <HeartBpm hr={hrOf(b.behind)} align="right" />
        </div>
      )}
      <div className={`og-trend ${closing ? 'is-closing' : opening ? 'is-opening' : ''}`}>
        {trend ? `${closing ? '▼' : opening ? '▲' : '●'} ${trend.delta > 0 ? '+' : ''}${trend.delta.toFixed(2)}s negli ultimi ${trend.seconds}s` : 'Gap in osservazione'}
      </div>
    </Plate>
  );
}

function TopSpeedScene({ speeds }) {
  const list = speeds.slice(0, 3);
  return (
    <Plate kicker="Top speed" title="Velocità massime">
      {list.length === 0 ? <div className="og-empty">In attesa di telemetria</div> : (
        <div className="og-rows og-rows--speed">
          {list.map((s, i) => (
            <div className="og-row" key={s.id}>
              <span className={`og-pos og-pos--${i + 1}`}>{i + 1}</span>
              <span className="og-color" style={{ background: s.color || '#fff' }} />
              <span className="og-name"><strong>{s.tag || '—'}</strong><span>{s.name}</span></span>
              <span className="og-speed">{Math.round(s.max)}<small>km/h</small></span>
            </div>
          ))}
        </div>
      )}
    </Plate>
  );
}

function SectorsScene({ bestSectors, ideal, globalBest }) {
  const S = ['S1', 'S2', 'S3'];
  const colors = _keySafe ? ['#ef4444', '#f59e0b', '#38bdf8'] : ['#ef4444', '#22c55e', '#38bdf8'];
  return (
    <Plate kicker="Settori" title="Migliori settori">
      <div className="og-sectors">
        {S.map((k, i) => {
          const b = bestSectors[k];
          return (
            <div className="og-sector" key={k} style={{ '--sec': colors[i] }}>
              <span className="og-sector__label">{k}</span>
              <span className="og-sector__time">{b ? formatLap(b.time) : '—'}</span>
              <span className="og-sector__who">{b ? shortName(b.driver) : ''}</span>
            </div>
          );
        })}
      </div>
      <div className="og-sectors__foot">
        <span>GIRO IDEALE <strong>{ideal != null ? formatLap(ideal) : '—'}</strong></span>
        <span>BEST LAP <strong className="is-fast">{globalBest != null ? formatLap(globalBest) : '—'}</strong></span>
      </div>
    </Plate>
  );
}

function PodiumScene({ drivers, finished }) {
  const top = drivers.slice(0, 3);
  return (
    <Plate kicker={finished ? 'Classifica finale' : 'Top 3'} title={finished ? 'Podio' : 'Posizioni'} wide>
      {top.length === 0 ? <div className="og-empty">In attesa dati</div> : (
        <div className="og-podium">
          {top.map((d, i) => (
            <div className={`og-podium__slot og-podium__slot--${i + 1}`} key={keyOf(d)}>
              <span className={`og-pos og-pos--${i + 1}`}>{i + 1}</span>
              <span className="og-color" style={{ background: paint(colorFromName(d.fullName || d.tag || d.mac)) }} />
              <strong>{displayName(d)}</strong>
              <span>{cleanTeam(d.team) || d.formula?.label || ''}</span>
              <em>{i === 0 ? (Number.isFinite(d.bestLapTime) ? `BEST ${formatLap(d.bestLapTime)}` : 'LEADER') : (d.gapToLeader || '')}</em>
            </div>
          ))}
        </div>
      )}
    </Plate>
  );
}

function DriverScene({ driver, globalBest, totalLaps }) {
  if (!driver) return <Plate kicker="Pilota" title="—"><div className="og-empty">Nessun pilota</div></Plate>;
  const color = paint(colorFromName(driver.fullName || driver.tag || driver.mac));
  const fast = globalBest != null && Number.isFinite(driver.bestLapTime) && Math.abs(driver.bestLapTime - globalBest) < 0.0005;
  return (
    <Plate kicker={`P${driver.position ?? '—'} · ${cleanTeam(driver.team) || driver.formula?.label || 'Pilota'}`} className="og-driver" accent="red">
      <div className="og-driver__head" style={{ '--driver': color }}>
        <span className="og-driver__bar" />
        <div>
          <span className="og-driver__tag">{driver.tag}</span>
          <span className="og-driver__name">{displayName(driver)}</span>
        </div>
        {driver.inPit && <span className="og-pit">PIT</span>}
      </div>
      <div className={`og-cells ${hrOf(driver) != null ? 'og-cells--5' : ''}`}>
        <div className="og-cell"><span>Giri</span><strong>{driver.lapCount ?? 0}{totalLaps ? <small>/{totalLaps}</small> : null}</strong></div>
        <div className="og-cell"><span>Ultimo</span><strong>{formatLap(driver.lastLapTime)}</strong></div>
        <div className={`og-cell ${fast ? 'is-fast' : ''}`}><span>Best</span><strong>{formatLap(driver.bestLapTime)}</strong></div>
        <div className="og-cell"><span>Gap</span><strong>{driver.position === 1 ? 'LEADER' : (driver.gapToLeader || '—')}</strong></div>
        {hrOf(driver) != null && (
          <div className="og-cell og-cell--hr" style={{ '--hr': hrZone(hrOf(driver))?.color || '#ff2d55', '--beat': `${(60 / hrOf(driver)).toFixed(3)}s` }}>
            <span>Battito</span>
            <strong><Icon.Heart size={13} className="og-heart" />{hrOf(driver)}<small>bpm</small></strong>
          </div>
        )}
      </div>
    </Plate>
  );
}

// Battito compatto (duello): cuore che pulsa al ritmo reale.
function HeartBpm({ hr, align = 'left' }) {
  if (hr == null) return <span className={`og-bpm og-bpm--na og-bpm--${align}`}>—</span>;
  const zone = hrZone(hr);
  return (
    <span className={`og-bpm og-bpm--${align}`} style={{ '--hr': zone?.color || '#ff2d55', '--beat': `${(60 / hr).toFixed(3)}s` }}>
      <Icon.Heart size={13} className="og-heart" />{hr}<small>bpm</small>
    </span>
  );
}

function HeartScene({ hearts, rows = 5 }) {
  const list = hearts.slice(0, rows);
  if (list.length === 0) return <Plate kicker="Cardio" title="Battito piloti" accent="heart"><div className="og-empty">Nessuna fascia cardio agganciata</div></Plate>;
  return (
    <Plate kicker="Cardio · Live" title="Battito piloti" accent="heart">
      <div className="og-rows og-rows--heart">
        {list.map((h, i) => {
          const d = h.driver;
          return (
            <div className="og-row" key={keyOf(d)} style={{ '--hr': h.zone?.color || 'rgba(255,255,255,0.35)', '--beat': h.hr ? `${(60 / h.hr).toFixed(3)}s` : '0s' }}>
              <span className={`og-pos ${i === 0 && h.hr != null ? 'og-pos--hr' : ''}`}>{d.position ?? '—'}</span>
              <span className="og-color" style={{ background: paint(colorFromName(d.fullName || d.tag || d.mac)) }} />
              <span className="og-name"><strong>{shortName(d)}</strong><span>{displayName(d)}</span></span>
              <span className="og-hr-zone">
                <span className="og-hr-zone__bar">{[1, 2, 3, 4, 5].map(z => <i key={z} className={h.zone && z <= h.zone.z ? 'is-on' : ''} />)}</span>
                <span className="og-hr-zone__label">{h.zone ? h.zone.label : 'IN ATTESA'}</span>
              </span>
              <span className="og-hr-val">
                <span className="og-hr-val__num">
                  {h.hr != null
                    ? <><Icon.Heart size={14} className="og-heart" />{h.hr}<small>bpm</small></>
                    : <span className="og-hr-val__na">—</span>}
                </span>
                <em>MAX {h.hrMax ?? '—'}</em>
              </span>
            </div>
          );
        })}
      </div>
      <div className="og-sectors__foot"><span>ZONE SU FCMAX {HR_MAX_REF}</span><span>MEDIA SESSIONE <strong>{(() => { const v = list.map(h => h.hrAvg).filter(Number.isFinite); return v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : '—'; })()}</strong></span></div>
    </Plate>
  );
}

function LapEventScene({ event }) {
  if (!event) return null;
  return (
    <Plate kicker={event.fastest ? '● GIRO VELOCE' : event.pb ? 'Personal best' : `Giro ${event.lapNum}`} accent={event.fastest ? 'purple' : event.pb ? 'lime' : 'red'} className="is-flash">
      <div className="og-big og-big--sm">
        <span className="og-big__time">{formatLap(event.lapTime)}</span>
        <span className="og-big__who">
          <span className="og-color" style={{ background: event.color }} />
          <strong>{event.name}</strong>
          <span>P{event.position ?? '—'}{cleanTeam(event.team) ? ` · ${cleanTeam(event.team)}` : ''} · GIRO {event.lapNum}</span>
        </span>
      </div>
    </Plate>
  );
}

/* ---------------------------------------------------------------------------
   Regia automatica riusabile: rotazione delle scene e interruzioni su evento.
   La usano sia questa pagina sia le dirette social simulate (SocialOverlay).
--------------------------------------------------------------------------- */
export function useAutoRotation({ stats, events, interval = 9000, auto = true, sceneParam = null }) {
  const [rotIdx, setRotIdx] = useState(0);
  const [override, setOverride] = useState(null);   // { scene, until, event }
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!auto) return undefined;
    const t = setInterval(() => setRotIdx(i => i + 1), interval);
    return () => clearInterval(t);
  }, [auto, interval]);
  useEffect(() => { const t = setInterval(() => setTick(x => x + 1), 1000); return () => clearInterval(t); }, []);
  const lastEventRef = useRef(null);
  useEffect(() => {
    const ev = events[0];
    if (!ev || ev.ts === lastEventRef.current) return;
    lastEventRef.current = ev.ts;
    if (ev.fastest || ev.pb) setOverride({ scene: ev.fastest ? 'fastest' : 'lapevent', until: ev.ts + EVENT_TTL_MS, event: ev });
  }, [events]);

  const available = useMemo(() => ROTATION.filter(s => {
    if (s === 'battle') return stats.battles.length > 0;
    if (s === 'topspeed') return stats.speeds.length > 0;
    if (s === 'sectors') return !!stats.bestSectors.S1;
    if (s === 'fastest') return !!stats.fastest;
    if (s === 'heart') return stats.hearts.length > 0;
    if (s === 'driver' || s === 'gaps') return stats.drivers.length > 0;
    return true;
  }), [stats]);

  const now = Date.now();
  const activeOverride = override && override.until > now ? override : null;
  const scene = !auto
    ? sceneParam
    : (activeOverride ? activeOverride.scene : available[rotIdx % Math.max(1, available.length)] || 'status');
  return { scene, activeOverride, rotIdx, now };
}

/** Una scena della regia, dato lo stato della gara. */
export function renderScene(scene, ctx) {
  const { snapshot, stats, events, focusDriver, rows, battleMaxGap, activeOverride, fastestIsNew, finished, now } = ctx;
  switch (scene) {
    case 'status': return <StatusScene snapshot={snapshot} leader={stats.leader} />;
    case 'gaps': return <GapsScene drivers={stats.drivers} rows={rows} globalBest={stats.globalBest} />;
    case 'fastest': return <FastestScene fastest={stats.fastest} globalBest={stats.globalBest} lapNum={stats.fastestLapNum} isNew={fastestIsNew} />;
    case 'battle': return <BattleScene battles={stats.battles} maxGap={battleMaxGap} />;
    case 'topspeed': return <TopSpeedScene speeds={stats.speeds} />;
    case 'sectors': return <SectorsScene bestSectors={stats.bestSectors} ideal={stats.ideal} globalBest={stats.globalBest} />;
    case 'podium': return <PodiumScene drivers={stats.drivers} finished={finished} />;
    case 'driver': return <DriverScene driver={focusDriver} globalBest={stats.globalBest} totalLaps={snapshot.totalLaps} />;
    case 'heart': return <HeartScene hearts={stats.hearts} rows={rows} />;
    case 'lapevent': return <LapEventScene event={activeOverride?.event || events.find(e => now - e.ts < EVENT_TTL_MS) || null} />;
    default: return <StatusScene snapshot={snapshot} leader={stats.leader} />;
  }
}

/**
 * Regia automatica pronta da incastonare altrove (dirette social simulate):
 * si collega da sola al WebSocket e ruota le scene come /obs/graphics.
 */
export function AutoGraphics({ interval = 9000, rows = 6, battleMaxGap = BATTLE_MAX_GAP_S, keySafe = true, className = '' }) {
  _keySafe = keySafe;
  const { snapshot, topSpeeds, events, gapTrend } = useRaceFeed();
  const stats = useStats(snapshot, topSpeeds, gapTrend, battleMaxGap);
  const { scene, activeOverride, rotIdx, now } = useAutoRotation({ stats, events, interval });
  if (!snapshot) return null;
  const fastestIsNew = events.some(e => e.fastest && now - e.ts < EVENT_TTL_MS);
  const finished = String(snapshot?.raceStatus || '').toUpperCase() === 'FINITA';
  const content = renderScene(scene, {
    snapshot, stats, events, focusDriver: stats.leader, rows, battleMaxGap,
    activeOverride, fastestIsNew, finished, now,
  });
  return (
    <div className={`og-scene ${className}`} key={`${scene}-${activeOverride ? activeOverride.until : rotIdx}`}>
      {content}
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Pagina
--------------------------------------------------------------------------- */
export default function ObsGraphics() {
  const { scene: sceneParam } = useParams();
  const [params] = useSearchParams();
  const bg = (params.get('bg') || 'green').toLowerCase();   // green screen di default
  _keySafe = bg === 'green';
  const vertical = isVerticalFormat(params);
  const pos = params.get('pos') || (sceneParam === 'status' ? 'bc' : 'bl');
  const interval = Math.max(4, Number(params.get('interval')) || 9) * 1000;
  const rows = Math.max(3, Math.min(20, Number(params.get('rows')) || 8));
  // Verticale: la grafica vive in una fascia bassa, quindi il numero di righe è
  // quello che ci sta (?rows= resta prioritario).
  const vpos = verticalPos(params.get('pos'));
  const vBox = vertical ? stageBox(params, vpos) : null;
  const driverId = params.get('id');
  const battleMaxGap = Math.max(0.1, Number(params.get('battle')) || BATTLE_MAX_GAP_S);

  const { snapshot, topSpeeds, events, gapTrend } = useRaceFeed();
  const stats = useStats(snapshot, topSpeeds, gapTrend, battleMaxGap);
  const auto = !sceneParam || sceneParam === 'auto';

  // Rotazione automatica + interruzioni evento (condivise con le dirette social)
  const { scene, activeOverride, rotIdx, now } = useAutoRotation({ stats, events, interval, auto, sceneParam });
  const focusDriver = driverId ? stats.drivers.find(d => keyOf(d) === driverId || d.mac === driverId) : stats.leader;
  const fastestIsNew = events.some(e => e.fastest && now - e.ts < EVENT_TTL_MS);
  const finished = String(snapshot?.raceStatus || '').toUpperCase() === 'FINITA';

  useEffect(() => {
    const prevHtml = document.documentElement.style.background, prevBody = document.body.style.background;
    const color = bg === 'green' ? '#00ff00' : bg === 'dark' ? '#0a0a0c' : 'transparent';
    document.documentElement.style.background = color; document.body.style.background = color;
    return () => { document.documentElement.style.background = prevHtml; document.body.style.background = prevBody; };
  }, [bg]);

  const rootCls = `og-root og-root--${bg} ${vertical ? 'og-root--vertical' : `og-anchor--${pos}`}`;
  if (!snapshot) return <div className={rootCls} />;

  // righe che entrano nella fascia: testata 40, piede 26 (cardio), marchio 40 in regia
  const vFree = vBox ? vBox.band - 40 - (auto ? 40 : 0) : 0;
  const askedRows = Number(params.get('rows')) > 0;
  const rowsGaps = vertical && !askedRows ? Math.max(3, Math.min(12, Math.floor(vFree / 32))) : rows;
  const rowsHeart = vertical && !askedRows ? Math.max(2, Math.min(8, Math.floor((vFree - 26) / 44))) : Math.min(rows, 8);

  const content = renderScene(scene, {
    snapshot, stats, events, focusDriver, battleMaxGap, activeOverride, fastestIsNew, finished, now,
    rows: scene === 'heart' ? rowsHeart : rowsGaps,
  });

  const body = (
    <>
      <div className="og-scene" key={`${scene}-${activeOverride ? activeOverride.until : rotIdx}`}>
        {content}
      </div>
      {auto && (
        <div className="og-brand"><img src={logoRaceSense} alt="RaceSense Live" className="og-brand__logo" /></div>
      )}
    </>
  );

  if (vertical) {
    return (
      <div className={rootCls}>
        <VerticalStage box={vBox} className={`ov-stage--${vpos} og-vstage og-vstage--${vpos}`}>
          {body}
        </VerticalStage>
      </div>
    );
  }

  return <div className={rootCls}>{body}</div>;
}
